import { useState } from "react";
import {
  createTraitInlineAction,
  type TraitInlineChoice,
} from "@/actions/traits";
import { InlineCreate } from "@/components/InlineCreate";
import { type TraitChoice, TraitCombobox } from "./TraitCombobox";
import styles from "./TraitFields.module.css";

export type TraitSelection = {
  requiresIds: number[];
  grantsIds: number[];
  conflictIds: number[];
  archivedIds: number[];
};

type TraitFieldsProps = {
  traits: TraitChoice[];
  requiresIds: number[];
  grantsIds: number[];
  errors?: string[];
  // The wrapping form owns the submit button, and an archived binding has to
  // disable it — so the selection is reported outward rather than kept here.
  onSelectionChange?: (selection: TraitSelection) => void;
};

// The controls only, with no form of their own. Both sets live in one
// component because requiring and granting are one authoring decision: split
// apart, a template can sit requiring a trait it also grants, which is
// uncompletable by construction and only caught at publish.
export const TraitFields = ({
  traits,
  requiresIds,
  grantsIds,
  errors,
  onSelectionChange,
}: TraitFieldsProps) => {
  const [requires, setRequires] = useState(requiresIds);
  const [grants, setGrants] = useState(grantsIds);
  // Seeded from the server list and appended to locally: a trait minted here
  // has to appear in both pickers without a revalidate, which would refresh
  // the route out from under a template that is not saved yet.
  const [choices, setChoices] = useState(traits);

  // The publish gate calls this self-gating and blocks on it. Saying so here
  // costs a glance; saying it only at publish costs a round trip.
  const conflictIds = requires.filter((id) => grants.includes(id));

  const archivedIds = [...new Set([...requires, ...grants])].filter(
    (id) => choices.find((choice) => choice.id === id)?.isArchived === true,
  );

  const conflictNames = conflictIds.map(
    (id) => choices.find((choice) => choice.id === id)?.code ?? String(id),
  );

  const report = (nextRequires: number[], nextGrants: number[]) => {
    const nextConflicts = nextRequires.filter((id) => nextGrants.includes(id));

    onSelectionChange?.({
      requiresIds: nextRequires,
      grantsIds: nextGrants,
      conflictIds: nextConflicts,
      archivedIds: [...new Set([...nextRequires, ...nextGrants])].filter(
        (id) => choices.find((choice) => choice.id === id)?.isArchived === true,
      ),
    });
  };

  // A trait minted from a picker is one the author reached for, so it lands in
  // the set whose search came back empty. The zero-trait case has no set to
  // land in and only adds it to the vocabulary.
  const addChoice = (
    choice: TraitInlineChoice,
    target?: "requires" | "grants",
  ) => {
    setChoices((current) => [...current, { ...choice, isArchived: false }]);

    if (target === "requires") {
      const nextRequires = [...requires, choice.id];

      setRequires(nextRequires);
      report(nextRequires, grants);

      return;
    }

    if (target === "grants") {
      const nextGrants = [...grants, choice.id];

      setGrants(nextGrants);
      report(requires, nextGrants);
    }
  };

  const renderMint = (target: "requires" | "grants") => (query: string) => (
    <InlineCreate
      triggerLabel={`No match — create "${query}"`}
      title="Create a trait"
      note="A trait is one yes/no fact about a person, and templates gate on it. Two spellings make a gate that matches for some people and not others, with nothing in the data explaining why. The signup question and its order are set on the Traits page."
      labelPlaceholder="Plays guitar"
      codePlaceholder="PLAYS_GUITAR"
      existing={choices}
      initialLabel={query}
      action={createTraitInlineAction}
      onCreated={(choice) => addChoice(choice, target)}
    />
  );

  if (choices.length === 0) {
    return (
      <div className={styles.group}>
        <p className={styles.lede}>
          No traits exist yet. Most templates need none — create one only if
          this quest must be gated or must hand something over.
        </p>
        <InlineCreate
          triggerLabel="New trait"
          title="Create a trait"
          note="A trait is one yes/no fact about a person, and templates gate on it. It is created here unbound: pick which set it belongs to once it exists."
          labelPlaceholder="Plays guitar"
          codePlaceholder="PLAYS_GUITAR"
          existing={choices}
          action={createTraitInlineAction}
          onCreated={(choice) => addChoice(choice)}
        />
      </div>
    );
  }

  return (
    <div className={styles.group}>
      <div className={styles.columns}>
        <TraitCombobox
          name="requiresTraitIds"
          legend="Requires"
          note="Never offered unless the user already holds every one of these."
          placeholder="Search traits to require"
          traits={choices}
          selectedIds={requires}
          conflictIds={conflictIds}
          onChange={(ids) => {
            setRequires(ids);
            report(ids, grants);
          }}
          renderMint={renderMint("requires")}
        />

        <TraitCombobox
          name="grantsTraitIds"
          legend="Grants"
          note="Handed over on completion, which is how the gated templates above ever become reachable."
          placeholder="Search traits to grant"
          traits={choices}
          selectedIds={grants}
          conflictIds={conflictIds}
          onChange={(ids) => {
            setGrants(ids);
            report(requires, ids);
          }}
          renderMint={renderMint("grants")}
        />
      </div>

      {conflictIds.length === 0 ? null : (
        <output className={styles.warning}>
          {conflictNames.join(", ")} sits in both sets. A template that requires
          what it grants is uncompletable, and the publish gate will refuse it.
        </output>
      )}

      {archivedIds.length === 0 ? null : (
        <p className={styles.error} role="alert">
          An archived trait is still bound here. Remove it before saving —
          archived traits cannot be re-bound.
        </p>
      )}

      {errors === undefined ? null : (
        <p className={styles.error} role="alert">
          {errors.join(" ")}
        </p>
      )}
    </div>
  );
};
